import { create } from "zustand";
import { createApi } from "../../../../utils/createApi";
import { markSiteUnsynced } from "../../../../utils/syncStore";
import { addIgnoreLabel } from "../SiteAboutPageUtils/addIgnoreLabel";
import { IgnoreZone } from "../SiteAboutPageUtils/siteAboutInterfaces";
import { MetricSimplifiedRow } from "../SiteAboutPageUtils/bulkUpdateCamerasApi";
import { useSiteAboutStore } from "../siteAboutStore/siteAboutStore";
import {
  CameraContext,
  FlexCameraSummary,
  FlexPreset,
  FlexScheduleSummary,
  FlexWizardStep,
  PendingEditorContext,
} from "./types";
import { buildPresetZonePayload } from "./flexIgnoreZonesOnboardingPayload";
import {
  FLEX_IZ_DEFAULT_CAMERA_CONFIG_OPTION,
  FLEX_IZ_NO_IGNORE_ZONES_OPTION,
} from "./flexIzPresetSelectConstants";
import {
  dedupeFlexPresetsByDisplayName,
  filterBundleTitleZones,
  normalizeFlexPresetSelectionToApiBackedMenuValue,
} from "./flexPresetDisplayDedupe";
import {
  FlexPresetConflict,
  FlexScheduleWithTimes,
  findPresetConflicts,
  presetConflictMessagesFromError,
} from "./flexPresetConflicts";

export {
  collectPersistedPresetIdsForCameras,
  collectPersistedPresetZoneIdsForCameras,
} from "./flexIgnoreZonesPresetCleanup";

const formatAdjustedDimensions = (dimensions: string) => {
  const [width, height] = dimensions.split(" x ").map(Number);
  if (!width || !height) {
    return { x: 720, y: 405 };
  }
  const ratio = height / width;
  return { x: 720, y: Math.round(720 * ratio) };
};

const scheduleCameraKey = (scheduleId: number, cameraId: number) =>
  `${scheduleId}:${cameraId}`;
const buildDefaultPresetName = (cameraName?: string | null) => {
  const normalizedCameraName = (cameraName || "").trim();
  return normalizedCameraName.length > 0
    ? `Default Preset ${normalizedCameraName}`
    : "Default Preset";
};
const isDefaultPresetName = (presetName: string, expectedName: string) =>
  presetName === expectedName ||
  presetName === "Default Preset" ||
  presetName === "Default Preset 1";

const nextPresetName = (presets: FlexPreset[]) => {
  let index = 2;
  while (presets.some((preset) => preset.name === `Preset ${index}`)) {
    index += 1;
  }
  return `Preset ${index}`;
};

const cloneIgnoreZones = (zones: IgnoreZone[]): IgnoreZone[] =>
  zones.map((zone) => ({
    ...zone,
    coordinates: zone.coordinates.map((coordinateGroup) =>
      coordinateGroup.map((coordinate) => coordinate)
    ),
  }));

const parsePresetListFromResponse = (data: unknown): unknown[] => {
  if (data && typeof data === "object" && Array.isArray((data as { results?: unknown }).results)) {
    return (data as { results: unknown[] }).results;
  }
  if (Array.isArray(data)) {
    return data;
  }
  return [];
};

/** `id → name` for every row of `GET preset/?camera=` (missing names become "Preset #<id>"). */
const presetNamesFromList = (rows: unknown[]): Record<number, string> => {
  const names: Record<number, string> = {};
  rows.forEach((row) => {
    const id = Number((row as { id?: unknown })?.id);
    if (!Number.isFinite(id)) {
      return;
    }
    const name = String((row as { name?: unknown })?.name ?? "").trim();
    names[id] = name.length > 0 ? name : `Preset #${id}`;
  });
  return names;
};

/**
 * Site About overlays expect each zone's `coordinates` to be a list of polygons
 * (`number[][][]`). Camera ignore-zone APIs and preset-zone payloads sometimes
 * return a single polygon as `number[][]`; normalize so polygons render.
 */
const coerceCoordinatesToPolygonList = (raw: unknown): number[][][] => {
  if (!Array.isArray(raw) || raw.length === 0) {
    return [];
  }
  const first = raw[0];
  if (!Array.isArray(first)) {
    return [];
  }
  const firstFirst = first[0];
  if (typeof firstFirst === "number") {
    return [raw as number[][]];
  }
  if (Array.isArray(firstFirst) && typeof firstFirst[0] === "number") {
    return raw as number[][][];
  }
  return [];
};

const presetZoneLabelDisplayName = (zone: { name?: string }) => {
  const raw = (zone.name || "").trim();
  if (!raw) {
    return "Preset zone";
  }
  return raw.replace(/\s*#\d+\s*$/u, "").trim() || raw;
};

const normalizeZoneName = (value: string) =>
  value.trim().toLowerCase().replace(/^ignore[_\s]+/, "").replace(/\s+/g, " ");

const pickDefaultZoneTemplate = (
  defaultZones: IgnoreZone[],
  candidateName: string,
  metricId?: number
) => {
  const byMetric =
    typeof metricId === "number" && Number.isFinite(metricId)
      ? defaultZones.find((zone) => Number(zone.metric_id) === metricId)
      : undefined;
  if (byMetric) {
    return byMetric;
  }
  const normalizedCandidate = normalizeZoneName(candidateName || "");
  if (!normalizedCandidate) {
    return undefined;
  }
  return defaultZones.find((zone) => {
    const displayName = normalizeZoneName(zone.label_display_name || "");
    const labelName = normalizeZoneName(zone.label || "");
    return displayName === normalizedCandidate || labelName === normalizedCandidate;
  });
};

const pickIgnoreAllTemplate = (defaultZones: IgnoreZone[]): IgnoreZone | undefined =>
  defaultZones.find((zone) => Number(zone.metric_id) === 0) ||
  defaultZones.find((zone) => Number(zone.zone_type) === 0) ||
  defaultZones.find((zone) => normalizeZoneName(zone.label || "") === "all") ||
  defaultZones.find(
    (zone) => normalizeZoneName(zone.label_display_name || "") === "all"
  ) ||
  defaultZones.find((zone) => normalizeZoneName(zone.label || "") === "motion") ||
  defaultZones.find(
    (zone) => normalizeZoneName(zone.label_display_name || "") === "motion"
  );

const polygonSignature = (polygon: number[][]) => JSON.stringify(polygon || []);

interface FlexIgnoreZonesOnboardingState {
  enabledBySite: Record<number, boolean>;
  flexIgnoreZonesEnabledSyncLoadingBySite: Record<number, boolean>;
  flexIgnoreZonesEnabledSyncErrorBySite: Record<number, string | undefined>;
  isOpen: boolean;
  siteId: number | null;
  schedules: FlexScheduleSummary[];
  cameras: FlexCameraSummary[];
  step: FlexWizardStep;
  activeScheduleIndex: number;
  activeCameraIndex: number;
  selectedCameraIdsBySchedule: Record<number, number[]>;
  selectedPresetByScheduleCamera: Record<string, string>;
  cameraContexts: Record<number, CameraContext>;
  presetsByCamera: Record<number, FlexPreset[]>;
  /**
   * Every preset id → name from `GET preset/?camera=`, keyed by camera. Unlike
   * `presetsByCamera` (the zone-bundle list, which display-name dedupe can
   * collapse), this keeps all ids so an assignment can always show its real name.
   */
  presetNamesByCamera: Record<number, Record<number, string>>;
  cameraFirstResolvedPresetByCamera: Record<number, string>;
  hideDefaultPresetForCamera: Record<number, boolean>;
  pendingEditorContext: PendingEditorContext | null;
  submitLoading: boolean;
  submitError: string | null;
  backendContractNotes: string[];
  setEnabledForSite: (siteId: number, enabled: boolean) => void;
  syncFlexIgnoreZonesEnabledFromApi: (
    siteId: number,
    cameras: FlexCameraSummary[],
    flexSchedules?: FlexScheduleSummary[],
    /** When false (LaunchDarkly `showFlexIgnoreZones` off), skip all flex-ignore-zones API calls. */
    flexIgnoreZonesFeatureEnabled?: boolean
  ) => Promise<void>;
  initializeFromSite: (params: {
    siteId: number;
    schedules: FlexScheduleSummary[];
    cameras: FlexCameraSummary[];
  }) => void;
  closeDialog: () => void;
  setStep: (step: FlexWizardStep) => void;
  setActiveScheduleIndex: (index: number) => void;
  setActiveCameraIndex: (index: number) => void;
  setSelectedCamerasForSchedule: (scheduleId: number, cameraIds: number[]) => void;
  setSelectedPresetForScheduleCamera: (
    scheduleId: number,
    cameraId: number,
    presetLocalId: string
  ) => void;
  loadCameraContext: (cameraId: number, forceRefresh?: boolean) => Promise<void>;
  /** Make sure `presetNamesByCamera[cameraId][presetId]` is known, refetching the preset list once if not. */
  ensurePresetNamesLoaded: (cameraId: number, presetId: number) => Promise<void>;
  /**
   * POST preset/ + preset-zone/ for a local draft (persistedId null).
   * Used from Site About camera row / offboarding when not assigning via onboarding wizard.
   */
  persistFlexPresetDraftToApi: (
    cameraId: number,
    presetLocalId: string
  ) => Promise<{ ok: true; newLocalId: string } | { ok: false; error: string }>;
  createCustomPresetDraft: (
    cameraId: number,
    defaultZones: IgnoreZone[]
  ) => string;
  /** Drop an unsaved custom preset (no `persistedId`), e.g. when canceling the create dialog. */
  removeLocalPresetDraft: (cameraId: number, presetLocalId: string) => void;
  updatePresetName: (
    cameraId: number,
    presetLocalId: string,
    name: string,
    markAsUserNamed?: boolean
  ) => void;
  updatePresetZones: (
    cameraId: number,
    presetLocalId: string,
    zones: IgnoreZone[]
  ) => void;
  setPendingEditorContext: (context: PendingEditorContext | null) => void;
  finalizePresetDecisionForCamera: (
    cameraId: number,
    presetLocalId: string,
    firstTimeDefaultOnly: boolean
  ) => void;
  getCurrentSchedule: () => FlexScheduleSummary | undefined;
  getSelectedCamerasForSchedule: (scheduleId: number) => number[];
  getPresetsForCamera: (cameraId: number) => FlexPreset[];
  getSelectedPresetForScheduleCamera: (
    scheduleId: number,
    cameraId: number
  ) => FlexPreset | undefined;
  submitAll: () => Promise<boolean>;
  /**
   * After flex schedule PATCH succeeds from Site About edit mode, POST each
   * camera-schedule-preset assignment from current store selections (same as onboarding submit).
   */
  persistFlexSchedulePresetAssignments: (
    siteId: number,
    flexSchedules: Array<{ id?: number }>
  ) => Promise<{ ok: true } | { ok: false; error: string }>;
  /**
   * ENG-1927: cross-schedule preset conflicts for the site's current
   * selections (Schedules edit path: saved preset ids decide "same preset").
   * Empty when Flex Ignore Zones is off for the site. Callers gate on fleet.
   */
  getPresetConflictsForSite: (
    siteId: number,
    flexSchedules: FlexScheduleWithTimes[]
  ) => FlexPresetConflict[];
  resetAll: () => void;
}

const initialState = {
  enabledBySite: {} as Record<number, boolean>,
  flexIgnoreZonesEnabledSyncLoadingBySite: {} as Record<number, boolean>,
  flexIgnoreZonesEnabledSyncErrorBySite: {} as Record<number, string | undefined>,
  isOpen: false,
  siteId: null as number | null,
  schedules: [] as FlexScheduleSummary[],
  cameras: [] as FlexCameraSummary[],
  step: "scheduleSelection" as FlexWizardStep,
  activeScheduleIndex: 0,
  activeCameraIndex: 0,
  selectedCameraIdsBySchedule: {} as Record<number, number[]>,
  selectedPresetByScheduleCamera: {} as Record<string, string>,
  cameraContexts: {} as Record<number, CameraContext>,
  presetsByCamera: {} as Record<number, FlexPreset[]>,
  presetNamesByCamera: {} as Record<number, Record<number, string>>,
  cameraFirstResolvedPresetByCamera: {} as Record<number, string>,
  hideDefaultPresetForCamera: {} as Record<number, boolean>,
  pendingEditorContext: null as PendingEditorContext | null,
  submitLoading: false,
  submitError: null as string | null,
  backendContractNotes: [] as string[],
};

export const useFlexIgnoreZonesOnboardingStore =
  create<FlexIgnoreZonesOnboardingState>((set, get) => ({
    ...initialState,
    setEnabledForSite: (siteId, enabled) => {
      set((state) => ({
        enabledBySite: {
          ...state.enabledBySite,
          [siteId]: enabled,
        },
      }));
    },
    syncFlexIgnoreZonesEnabledFromApi: async (
      siteId,
      cameras,
      flexSchedules,
      flexIgnoreZonesFeatureEnabled = true
    ) => {
      if (!flexIgnoreZonesFeatureEnabled) {
        return;
      }
      try {
        localStorage.removeItem(`flexIgnoreZonesEnabled_${siteId}`);
        set((state) => ({
          flexIgnoreZonesEnabledSyncLoadingBySite: {
            ...state.flexIgnoreZonesEnabledSyncLoadingBySite,
            [siteId]: true,
          },
          flexIgnoreZonesEnabledSyncErrorBySite: {
            ...state.flexIgnoreZonesEnabledSyncErrorBySite,
            [siteId]: undefined,
          },
        }));
        const AxiosUI = createApi("");
        const cameraIds = cameras.map((c) => c.id);
        let hasAnyPresets = false;
        for (const cameraId of cameraIds) {
          const res = await AxiosUI.get(`preset/?camera=${cameraId}`);
          const list = parsePresetListFromResponse(res.data);
          if (list.length > 0) {
            hasAnyPresets = true;
            break;
          }
        }
        get().setEnabledForSite(siteId, hasAnyPresets);
        if (hasAnyPresets) {
          const fallbackStoreSchedulesRaw = useSiteAboutStore.getState()
            .siteSchedules as Array<{ id?: number | string; display_name?: string }>;
          const fallbackStoreSchedules: FlexScheduleSummary[] = Array.isArray(
            fallbackStoreSchedulesRaw
          )
            ? fallbackStoreSchedulesRaw
                .map((s, idx) => ({
                  id: Number(s?.id ?? idx + 1),
                  display_name: s?.display_name || `Schedule ${idx + 1}`,
                }))
                .filter((s) => Number.isFinite(s.id))
            : [];
          const schedules = flexSchedules?.length
            ? flexSchedules
            : fallbackStoreSchedules.length > 0
            ? fallbackStoreSchedules
            : [];
          const cameraIdList = cameras.map((c) => c.id);
          const selectedCameraIdsBySchedule =
            schedules.length > 0
              ? schedules.reduce(
                  (acc, sch) => ({
                    ...acc,
                    [sch.id]: cameraIdList,
                  }),
                  {} as Record<number, number[]>
                )
              : undefined;
          set((state) => ({
            siteId,
            cameras,
            ...(schedules.length > 0 ? { schedules, selectedCameraIdsBySchedule } : {}),
          }));
          await Promise.all(cameraIds.map((id) => get().loadCameraContext(id, true)));
          if (schedules.length > 0) {
            const selectedPresetByScheduleCamera: Record<string, string> = {};
            for (const schedule of schedules) {
              for (const camera of cameras) {
                const key = scheduleCameraKey(schedule.id, camera.id);
                // No row, or a row with `preset: null`, means the camera runs its
                // own zones/lines under this schedule ("Default") — never a
                // synthesized preset name (ENG-1610).
                let presetLocalId: string = FLEX_IZ_DEFAULT_CAMERA_CONFIG_OPTION;
                try {
                  const assignRes = await AxiosUI.get("camera-schedule-preset/", {
                    params: { camera: camera.id, flex_schedule: schedule.id },
                  });
                  const rows = parsePresetListFromResponse(assignRes.data);
                  const firstRow = rows[0] as
                    | { preset?: number; camera?: number; flex_schedule?: number }
                    | undefined;
                  const presetsForCam = get().presetsByCamera[camera.id] || [];
                  const matchedRowByCameraPreset = rows.find((row) => {
                    const presetId = Number((row as { preset?: number })?.preset);
                    if (!Number.isFinite(presetId)) {
                      return false;
                    }
                    return presetsForCam.some((preset) => preset.persistedId === presetId);
                  }) as
                    | { preset?: number; camera?: number; flex_schedule?: number }
                    | undefined;
                  const assignmentRow = matchedRowByCameraPreset || firstRow;
                  if (assignmentRow && assignmentRow.preset != null) {
                    const pid = Number(assignmentRow.preset);
                    const match = presetsForCam.find((p) => p.persistedId === pid);
                    presetLocalId = match ? match.localId : `server-${pid}`;
                    if (!match) {
                      // Not in the zone-bundle list: make sure its real name is
                      // known so the schedule summary can label it.
                      await get().ensurePresetNamesLoaded(camera.id, pid);
                    }
                  }
                } catch {
                  // Keep Default if list endpoint or row is unavailable.
                }
                selectedPresetByScheduleCamera[key] = presetLocalId;
              }
            }
            set((state) => ({
              selectedPresetByScheduleCamera: {
                ...state.selectedPresetByScheduleCamera,
                ...selectedPresetByScheduleCamera,
              },
            }));
          }
        } else {
          set((state) => {
            const nextPresets = { ...state.presetsByCamera };
            cameraIds.forEach((id) => {
              delete nextPresets[id];
            });
            return { presetsByCamera: nextPresets };
          });
        }
      } catch (error: any) {
        set((state) => ({
          flexIgnoreZonesEnabledSyncErrorBySite: {
            ...state.flexIgnoreZonesEnabledSyncErrorBySite,
            [siteId]:
              error?.message || "Unable to sync Flex Ignore Zones state from the API.",
          },
          enabledBySite: {
            ...state.enabledBySite,
            [siteId]: false,
          },
        }));
      } finally {
        set((state) => ({
          flexIgnoreZonesEnabledSyncLoadingBySite: {
            ...state.flexIgnoreZonesEnabledSyncLoadingBySite,
            [siteId]: false,
          },
        }));
      }
    },
    initializeFromSite: ({ siteId, schedules, cameras }) => {
      const allCameraIds = cameras.map((camera) => camera.id);
      const selectedCameraIdsBySchedule = schedules.reduce(
        (acc, schedule) => ({
          ...acc,
          [schedule.id]: allCameraIds,
        }),
        {} as Record<number, number[]>
      );
      const selectedPresetByScheduleCamera = schedules.reduce((acc, schedule) => {
        cameras.forEach((camera) => {
          acc[scheduleCameraKey(schedule.id, camera.id)] = `default-${camera.id}`;
        });
        return acc;
      }, {} as Record<string, string>);
      set((state) => ({
        ...initialState,
        enabledBySite: {
          ...state.enabledBySite,
          [siteId]: state.enabledBySite[siteId] ?? true,
        },
        flexIgnoreZonesEnabledSyncLoadingBySite: state.flexIgnoreZonesEnabledSyncLoadingBySite,
        flexIgnoreZonesEnabledSyncErrorBySite: state.flexIgnoreZonesEnabledSyncErrorBySite,
        isOpen: true,
        siteId,
        schedules,
        cameras,
        selectedCameraIdsBySchedule,
        selectedPresetByScheduleCamera,
      }));
    },
    closeDialog: () =>
      set((state) => ({
        ...state,
        isOpen: false,
        step: "scheduleSelection",
        pendingEditorContext: null,
        submitError: null,
      })),
    setStep: (step) => set({ step }),
    setActiveScheduleIndex: (activeScheduleIndex) => set({ activeScheduleIndex }),
    setActiveCameraIndex: (activeCameraIndex) => set({ activeCameraIndex }),
    setSelectedCamerasForSchedule: (scheduleId, cameraIds) =>
      set((state) => ({
        selectedCameraIdsBySchedule: {
          ...state.selectedCameraIdsBySchedule,
          [scheduleId]: cameraIds,
        },
      })),
    setSelectedPresetForScheduleCamera: (scheduleId, cameraId, presetLocalId) =>
      set((state) => {
        useSiteAboutStore.getState().setScheduleChangesMade(true);
        return {
          selectedPresetByScheduleCamera: {
            ...state.selectedPresetByScheduleCamera,
            [scheduleCameraKey(scheduleId, cameraId)]: presetLocalId,
          },
        };
      }),
    ensurePresetNamesLoaded: async (cameraId, presetId) => {
      if (get().presetNamesByCamera[cameraId]?.[presetId] != null) {
        return;
      }
      try {
        const AxiosUI = createApi("");
        const res = await AxiosUI.get(`preset/?camera=${cameraId}`);
        const names = presetNamesFromList(parsePresetListFromResponse(res.data));
        set((state) => ({
          presetNamesByCamera: {
            ...state.presetNamesByCamera,
            [cameraId]: { ...(state.presetNamesByCamera[cameraId] || {}), ...names },
          },
        }));
      } catch {
        // Summary falls back to "Preset #<id>" when the name stays unknown.
      }
    },
    loadCameraContext: async (cameraId, forceRefresh = false) => {
      const currentContext = get().cameraContexts[cameraId];
      if (!forceRefresh && (currentContext?.loaded || currentContext?.loading)) {
        return;
      }
      const cameraFromSiteState = get().cameras.find((camera) => camera.id === cameraId);
      const fallbackDefaultZones = addIgnoreLabel(cameraFromSiteState?.ignore_zones || []);
      const fallbackPreviewUrl = cameraFromSiteState?.preview_url || "";
      set((state) => ({
        cameraContexts: {
          ...state.cameraContexts,
          [cameraId]: {
            loaded: false,
            loading: true,
            error: null,
            preview: fallbackPreviewUrl
              ? {
                  previewUrl: fallbackPreviewUrl,
                  adjustedDimensions: { x: 720, y: 405 },
                }
              : null,
            defaultZones: cloneIgnoreZones(fallbackDefaultZones),
          },
        },
      }));
      try {
        const AxiosUI = createApi("");
        const [statusResponse, ignoreZonesResponse, presetsResponse] =
          await Promise.all([
            AxiosUI.get(`/camera/${cameraId}/status/`),
            AxiosUI.get(`/camera/${cameraId}/ignore_zones/`),
            AxiosUI.get(`preset/?camera=${cameraId}`),
          ]);
        const defaultZones = addIgnoreLabel(ignoreZonesResponse.data || []);
        const existingPresetsRaw = parsePresetListFromResponse(presetsResponse.data);
        const existingPresets: FlexPreset[] = await Promise.all(
          existingPresetsRaw.map(async (preset: any) => {
            let mappedPresetZones: IgnoreZone[] = [];
            try {
              const presetZonesResponse = await AxiosUI.get(
                `preset-zone/?camera=${cameraId}&preset=${preset.id}`
              );
              const presetZonesRaw = Array.isArray(presetZonesResponse.data?.results)
                ? presetZonesResponse.data.results
                : Array.isArray(presetZonesResponse.data)
                ? presetZonesResponse.data
                : [];
              mappedPresetZones = presetZonesRaw.map((zone: any, zoneIndex: number) => {
                const nestedLabels = Array.isArray(zone?.labels)
                  ? zone.labels.filter(
                      (labelEntry: any) =>
                        labelEntry && typeof labelEntry === "object"
                    )
                  : [];
                if (nestedLabels.length > 0) {
                  const nestedLabelCoordinateLists = nestedLabels.map((labelEntry: any) =>
                    coerceCoordinatesToPolygonList(
                      labelEntry.coordinates ?? zone.coordinates
                    )
                  );
                  const coordinateSignatures = nestedLabels.map((labelEntry: any) =>
                    JSON.stringify(
                      coerceCoordinatesToPolygonList(
                        labelEntry.coordinates ?? zone.coordinates
                      )
                    )
                  );
                  const uniqueCoordinateSignatures = new Set(coordinateSignatures);
                  const polygonFrequency = new Map<string, number>();
                  const polygonBySignature = new Map<string, number[][]>();
                  nestedLabelCoordinateLists.forEach((polygonList: number[][][]) => {
                    const uniqueInLabel = new Set<string>();
                    polygonList.forEach((polygon: number[][]) => {
                      const sig = polygonSignature(polygon);
                      polygonBySignature.set(sig, polygon);
                      uniqueInLabel.add(sig);
                    });
                    uniqueInLabel.forEach((sig) => {
                      polygonFrequency.set(sig, (polygonFrequency.get(sig) || 0) + 1);
                    });
                  });
                  const sharedPolygonThreshold = Math.max(
                    2,
                    Math.ceil(nestedLabels.length / 2)
                  );
                  const sharedPolygonSignatures = Array.from(
                    polygonFrequency.entries()
                  )
                    .filter(([, count]) => count >= sharedPolygonThreshold)
                    .map(([sig]) => sig);
                  const sharedPolygons = sharedPolygonSignatures
                    .map((sig) => polygonBySignature.get(sig))
                    .filter((polygon): polygon is number[][] => Array.isArray(polygon));
                  let nestedMappedZones = nestedLabels
                    .map((labelEntry: any, labelIndex: number) => {
                      const labelName =
                        labelEntry.name ||
                        labelEntry.label_name ||
                        zone.name ||
                        "preset_zone";
                      const labelMetricId = Number(
                        labelEntry.label ?? labelEntry.label_id ?? zone.metric_id ?? 0
                      );
                      const baseTemplate = pickDefaultZoneTemplate(
                        defaultZones,
                        labelName,
                        Number.isFinite(labelMetricId) ? labelMetricId : undefined
                      );
                      const coords = coerceCoordinatesToPolygonList(
                        labelEntry.coordinates ?? zone.coordinates
                      );
                      const rawId = zone.id;
                      const presetZoneId =
                        rawId != null && Number.isFinite(Number(rawId))
                          ? Number(rawId)
                          : undefined;
                      const resolvedZoneType =
                        zone.zone_type ?? baseTemplate?.zone_type ?? 0;
                      const resolvedLineColor =
                        zone.line_color ||
                        baseTemplate?.line_color ||
                        "#FF8400";
                      return {
                        zone_type: resolvedZoneType,
                        stream_id: zone.stream_id ?? baseTemplate?.stream_id ?? 0,
                        stream_name: zone.stream_name ?? baseTemplate?.stream_name ?? "",
                        metric_id: Number.isFinite(labelMetricId)
                          ? labelMetricId
                          : baseTemplate?.metric_id || 0,
                        label: `${labelName}-${zoneIndex}-${labelIndex}`,
                        label_display_name:
                          baseTemplate?.label_display_name ||
                          presetZoneLabelDisplayName({ name: labelName }),
                        line_color: resolvedLineColor,
                        coordinates: coords as unknown as IgnoreZone["coordinates"],
                        ...(presetZoneId !== undefined
                          ? { preset_zone_id: presetZoneId }
                          : {}),
                      } as IgnoreZone;
                    })
                    .flat();
                  if (
                    uniqueCoordinateSignatures.size > 1 &&
                    sharedPolygons.length > 0 &&
                    nestedMappedZones.length > 0
                  ) {
                    const ignoreAllTemplate = pickIgnoreAllTemplate(defaultZones);
                    const sharedPolygonSignatureSet = new Set(
                      sharedPolygons.map((polygon) => polygonSignature(polygon))
                    );
                    const hasIgnoreAll = nestedMappedZones.some(
                      (zoneEntry: IgnoreZone) => Number(zoneEntry.zone_type) === 0
                    );
                    if (!hasIgnoreAll) {
                      nestedMappedZones.unshift({
                        zone_type: 0,
                        stream_id: zone.stream_id ?? ignoreAllTemplate?.stream_id ?? 0,
                        stream_name:
                          zone.stream_name ?? ignoreAllTemplate?.stream_name ?? "",
                        metric_id: ignoreAllTemplate?.metric_id ?? 0,
                        label: "ignore_all_inferred",
                        label_display_name:
                          ignoreAllTemplate?.label_display_name || "Ignore All",
                        line_color: ignoreAllTemplate?.line_color || "#ffff00",
                        coordinates: sharedPolygons as unknown as IgnoreZone["coordinates"],
                        ...(zone.id != null && Number.isFinite(Number(zone.id))
                          ? { preset_zone_id: Number(zone.id) }
                          : {}),
                      });
                    }
                    nestedMappedZones = nestedMappedZones
                      .map((zoneEntry: IgnoreZone) => {
                        if (Number(zoneEntry.zone_type) === 0) {
                          return {
                            ...zoneEntry,
                            zone_type: 0,
                            metric_id: ignoreAllTemplate?.metric_id ?? zoneEntry.metric_id,
                            label: "ignore_all_inferred",
                            label_display_name:
                              ignoreAllTemplate?.label_display_name || "Ignore All",
                            line_color:
                              ignoreAllTemplate?.line_color ||
                              zoneEntry.line_color ||
                              "#ffff00",
                            coordinates:
                              sharedPolygons as unknown as IgnoreZone["coordinates"],
                          };
                        }
                        const filteredCoordinates = zoneEntry.coordinates.filter(
                          (polygon: number[]) =>
                            !sharedPolygonSignatureSet.has(
                              polygonSignature(polygon as unknown as number[][])
                            )
                        );
                        return {
                          ...zoneEntry,
                          coordinates: filteredCoordinates,
                        };
                      })
                      .filter(
                        (zoneEntry: IgnoreZone) => zoneEntry.coordinates.length > 0
                      );
                  }
                  if (uniqueCoordinateSignatures.size === 1 && nestedMappedZones.length > 0) {
                    const ignoreAllTemplate = pickIgnoreAllTemplate(defaultZones);
                    const hasIgnoreAll = nestedMappedZones.some(
                      (zoneEntry: IgnoreZone) => Number(zoneEntry.zone_type) === 0
                    );
                    const inferredCoordinates = coerceCoordinatesToPolygonList(
                      nestedLabels[0]?.coordinates ?? zone.coordinates
                    );
                    if (!hasIgnoreAll) {
                      nestedMappedZones.unshift({
                        zone_type: 0,
                        stream_id: zone.stream_id ?? ignoreAllTemplate?.stream_id ?? 0,
                        stream_name:
                          zone.stream_name ?? ignoreAllTemplate?.stream_name ?? "",
                        metric_id: ignoreAllTemplate?.metric_id ?? 0,
                        label: "ignore_all_inferred",
                        label_display_name:
                          ignoreAllTemplate?.label_display_name || "Ignore All",
                        line_color: ignoreAllTemplate?.line_color || "#FF8400",
                        coordinates:
                          inferredCoordinates as unknown as IgnoreZone["coordinates"],
                        ...(zone.id != null && Number.isFinite(Number(zone.id))
                          ? { preset_zone_id: Number(zone.id) }
                          : {}),
                      });
                    }
                    const ignoreAllZone =
                      nestedMappedZones.find(
                        (zoneEntry: IgnoreZone) => Number(zoneEntry.zone_type) === 0
                      ) || nestedMappedZones[0];
                    nestedMappedZones = [
                      {
                        ...ignoreAllZone,
                        zone_type: 0,
                        label: "ignore_all_inferred",
                        label_display_name:
                          ignoreAllTemplate?.label_display_name || "Ignore All",
                        line_color:
                          ignoreAllZone.line_color ||
                          ignoreAllTemplate?.line_color ||
                          "#FF8400",
                        coordinates:
                          inferredCoordinates as unknown as IgnoreZone["coordinates"],
                      },
                    ];
                  }
                  return nestedMappedZones;
                }
                const rawId = zone.id;
                const presetZoneId =
                  rawId != null && Number.isFinite(Number(rawId)) ? Number(rawId) : undefined;
                const baseName = zone.name || "preset_zone";
                return {
                  zone_type: zone.zone_type || 0,
                  stream_id: zone.stream_id || 0,
                  stream_name: zone.stream_name || "",
                  metric_id: Array.isArray(zone.labels) ? zone.labels[0] || 0 : 0,
                  label: `${baseName}-${zoneIndex}`,
                  label_display_name: presetZoneLabelDisplayName(zone),
                  line_color: zone.line_color || "#FF8400",
                  coordinates: coerceCoordinatesToPolygonList(zone.coordinates) as unknown as IgnoreZone["coordinates"],
                  ...(presetZoneId !== undefined ? { preset_zone_id: presetZoneId } : {}),
                };
              }).flat();
            } catch (_error) {
              mappedPresetZones = [];
            }
            const presetName = preset.name || "Unnamed Preset";
            const filteredPresetZones = filterBundleTitleZones(
              presetName,
              mappedPresetZones
            );
            return {
              localId: `server-${preset.id}`,
              cameraId,
              name: presetName,
              zones: filteredPresetZones,
              persistedId: preset.id,
              source: "custom" as const,
              userNamed: true,
            };
          })
        );
        const defaultPresetName = buildDefaultPresetName(
          cameraFromSiteState?.camera_name || statusResponse.data?.camera_name
        );
        const priorDefaultSlot = (get().presetsByCamera[cameraId] || []).find(
          (p) => p.localId === `default-${cameraId}`
        );
        const priorDefaultPersistedIdNum =
          priorDefaultSlot?.persistedId != null &&
          Number.isFinite(Number(priorDefaultSlot.persistedId))
            ? Number(priorDefaultSlot.persistedId)
            : undefined;

        let existingDefaultPreset: FlexPreset | undefined;
        if (priorDefaultPersistedIdNum != null) {
          existingDefaultPreset = existingPresets.find(
            (preset) => Number(preset.persistedId) === priorDefaultPersistedIdNum
          );
        }
        if (existingDefaultPreset == null) {
          existingDefaultPreset = existingPresets.find((preset) =>
            isDefaultPresetName(preset.name, defaultPresetName)
          );
        }

        const existingCustomPresets = existingPresets.filter((preset) => {
          if (!existingDefaultPreset) {
            return true;
          }
          // Keep the canonical API `server-*` row for the default preset. The synthetic
          // `default-${cameraId}` slot is excluded from API-only dropdowns; without this row,
          // the real preset (e.g. "Default Preset …" from GET preset/?camera=) disappears.
          if (preset.localId === existingDefaultPreset.localId) {
            return true;
          }
          if (
            existingDefaultPreset.persistedId != null &&
            preset.persistedId != null &&
            Number(preset.persistedId) === Number(existingDefaultPreset.persistedId)
          ) {
            return false;
          }
          return true;
        });
        /** Default preset row on server uses `preset-zone/`; camera `ignore_zones/` can lag after PATCH — prefer API preset zones when present. */
        const defaultPresetZones =
          existingDefaultPreset != null
            ? cloneIgnoreZones(existingDefaultPreset.zones || [])
            : cloneIgnoreZones(defaultZones);
        const resolvedPresetName =
          existingDefaultPreset?.name ??
          priorDefaultSlot?.name ??
          defaultPresetName;
        const resolvedPersistedId =
          existingDefaultPreset != null ? existingDefaultPreset.persistedId : null;
        const defaultPreset: FlexPreset = {
          localId: `default-${cameraId}`,
          cameraId,
          name: resolvedPresetName,
          zones: defaultPresetZones,
          persistedId: resolvedPersistedId,
          source: "default",
          userNamed:
            Boolean(priorDefaultSlot?.userNamed) ||
            (existingDefaultPreset != null &&
              !isDefaultPresetName(existingDefaultPreset.name, defaultPresetName)),
        };
        const adjustedDimensions = formatAdjustedDimensions(
          statusResponse.data?.frame_dimensions || "16 x 9"
        );
        const presetsByCamera = get().presetsByCamera[cameraId] || [];
        const mergedPresets = [...presetsByCamera];
        const priorNoIgnoreSlot = mergedPresets.find(
          (p) => p.localId === FLEX_IZ_NO_IGNORE_ZONES_OPTION
        );
        const priorNoIgnorePersistedId =
          priorNoIgnoreSlot?.persistedId != null &&
          Number.isFinite(Number(priorNoIgnoreSlot.persistedId))
            ? Number(priorNoIgnoreSlot.persistedId)
            : null;
        const noIgnoreZonesPreset: FlexPreset = {
          localId: FLEX_IZ_NO_IGNORE_ZONES_OPTION,
          cameraId,
          name: "No Ignore Zones",
          zones: [],
          persistedId: priorNoIgnorePersistedId,
          source: "custom",
          userNamed: true,
        };
        [defaultPreset, ...existingCustomPresets, noIgnoreZonesPreset].forEach((preset) => {
          const idx = mergedPresets.findIndex((item) => item.localId === preset.localId);
          if (idx === -1) {
            mergedPresets.push(preset);
          } else {
            mergedPresets[idx] = preset;
          }
        });
        const finalPresets = dedupeFlexPresetsByDisplayName(cameraId, mergedPresets);
        set((state) => {
          const nextSelected = { ...state.selectedPresetByScheduleCamera };
          let selectedChanged = false;
          for (const key of Object.keys(nextSelected)) {
            if (!key.endsWith(`:${cameraId}`)) {
              continue;
            }
            const cur = nextSelected[key];
            const normalized = normalizeFlexPresetSelectionToApiBackedMenuValue(
              cameraId,
              finalPresets,
              cur
            );
            if (normalized !== cur) {
              nextSelected[key] = normalized;
              selectedChanged = true;
            }
          }
          return {
            cameraContexts: {
              ...state.cameraContexts,
              [cameraId]: {
                loaded: true,
                loading: false,
                error: null,
                preview: {
                  previewUrl:
                    statusResponse.data?.preview_url || fallbackPreviewUrl || "",
                  adjustedDimensions,
                },
                defaultZones: cloneIgnoreZones(
                  defaultZones.length ? defaultZones : fallbackDefaultZones
                ),
              },
            },
            presetsByCamera: {
              ...state.presetsByCamera,
              [cameraId]: finalPresets,
            },
            presetNamesByCamera: {
              ...state.presetNamesByCamera,
              [cameraId]: presetNamesFromList(existingPresetsRaw),
            },
            ...(selectedChanged
              ? { selectedPresetByScheduleCamera: nextSelected }
              : {}),
          };
        });
      } catch (error: any) {
        set((state) => ({
          cameraContexts: {
            ...state.cameraContexts,
            [cameraId]: {
              loaded: false,
              loading: false,
              error:
                error?.message ||
                "Unable to load camera preview and ignore zones for onboarding.",
              preview: null,
              defaultZones: [],
            },
          },
        }));
      }
    },
    persistFlexPresetDraftToApi: async (cameraId, presetLocalId) => {
      const preset = (get().presetsByCamera[cameraId] || []).find(
        (p) => p.localId === presetLocalId
      );
      if (!preset) {
        return { ok: false, error: "Preset not found." };
      }
      if (preset.persistedId != null) {
        return { ok: false, error: "This preset is already saved." };
      }
      const trimmedName = preset.name?.trim() || "";
      if (!trimmedName) {
        return { ok: false, error: "Preset name is required." };
      }
      const flexSiteId = get().siteId;
      const siteStore = useSiteAboutStore.getState();
      const customerId =
        flexSiteId != null && Number.isFinite(Number(flexSiteId))
          ? Number(flexSiteId)
          : siteStore.siteMetricsSimplifiedCustomerId;
      if (customerId == null || !Number.isFinite(Number(customerId))) {
        return {
          ok: false,
          error:
            "Site metrics are not loaded. Open this site on Site About and try again.",
        };
      }
      const customerIdNum = Number(customerId);
      const AxiosUI = createApi("");
      try {
        let metricRows: MetricSimplifiedRow[] =
          siteStore.siteMetricsSimplifiedCustomerId === customerIdNum
            ? siteStore.siteMetricsSimplified
            : [];
        if (metricRows.length === 0) {
          const metricSimplifiedResponse = await AxiosUI.get("metric/simplified/", {
            params: { customer: customerIdNum },
          });
          if (Array.isArray(metricSimplifiedResponse.data?.results)) {
            metricRows = metricSimplifiedResponse.data.results;
          } else if (Array.isArray(metricSimplifiedResponse.data)) {
            metricRows = metricSimplifiedResponse.data;
          } else {
            metricRows = [];
          }
          siteStore.setSiteMetricsSimplifiedData(metricRows, customerIdNum);
        }

        const presetBody = { camera: cameraId, name: trimmedName };
        const createdPresetResponse = await AxiosUI.post("preset/", presetBody);
        const createdPresetId = Number(createdPresetResponse.data?.id);
        if (!Number.isFinite(createdPresetId)) {
          return {
            ok: false,
            error: "Server did not return a preset id. Try again.",
          };
        }
        const presetZoneApiName = `${trimmedName} #${createdPresetId}`;
        const presetZonePayload = buildPresetZonePayload({
          cameraId,
          presetId: createdPresetId,
          presetName: presetZoneApiName,
          zones: preset.zones || [],
          metricRows,
        });
        await AxiosUI.post("preset-zone/", presetZonePayload);

        const newLocalId = `server-${createdPresetId}`;
        set((state) => {
          const presets = state.presetsByCamera[cameraId] || [];
          const nextPresets = presets.map((p) =>
            p.localId === presetLocalId
              ? {
                  ...p,
                  localId: newLocalId,
                  name: trimmedName,
                  persistedId: createdPresetId,
                  userNamed: true,
                }
              : p
          );
          return {
            presetsByCamera: {
              ...state.presetsByCamera,
              [cameraId]: dedupeFlexPresetsByDisplayName(cameraId, nextPresets),
            },
          };
        });
        return { ok: true, newLocalId };
      } catch (error: any) {
        return {
          ok: false,
          error:
            error?.message ||
            "Unable to save flex ignore zones preset to the server.",
        };
      }
    },
    createCustomPresetDraft: (cameraId, defaultZones) => {
      const presets = get().presetsByCamera[cameraId] || [];
      const presetName = nextPresetName(presets);
      const localId = `custom-${cameraId}-${Date.now()}-${Math.random()}`;
      const nextPreset: FlexPreset = {
        localId,
        cameraId,
        name: presetName,
        zones: cloneIgnoreZones(defaultZones),
        persistedId: null,
        source: "custom",
        userNamed: false,
      };
      set((state) => ({
        presetsByCamera: {
          ...state.presetsByCamera,
          [cameraId]: [...presets, nextPreset],
        },
      }));
      return localId;
    },
    removeLocalPresetDraft: (cameraId, presetLocalId) =>
      set((state) => {
        const presets = state.presetsByCamera[cameraId] || [];
        const target = presets.find((p) => p.localId === presetLocalId);
        if (!target || target.persistedId != null || target.source !== "custom") {
          return {};
        }
        return {
          presetsByCamera: {
            ...state.presetsByCamera,
            [cameraId]: presets.filter((p) => p.localId !== presetLocalId),
          },
        };
      }),
    updatePresetName: (cameraId, presetLocalId, name, markAsUserNamed = true) =>
      set((state) => {
        const presets = state.presetsByCamera[cameraId] || [];
        return {
          presetsByCamera: {
            ...state.presetsByCamera,
            [cameraId]: presets.map((preset) =>
              preset.localId === presetLocalId
                ? {
                    ...preset,
                    name,
                    userNamed: markAsUserNamed ? true : preset.userNamed,
                  }
                : preset
            ),
          },
        };
      }),
    updatePresetZones: (cameraId, presetLocalId, zones) =>
      set((state) => {
        const presets = state.presetsByCamera[cameraId] || [];
        return {
          presetsByCamera: {
            ...state.presetsByCamera,
            [cameraId]: presets.map((preset) =>
              preset.localId === presetLocalId
                ? { ...preset, zones: cloneIgnoreZones(zones) }
                : preset
            ),
          },
        };
      }),
    setPendingEditorContext: (pendingEditorContext) => set({ pendingEditorContext }),
    finalizePresetDecisionForCamera: (cameraId, presetLocalId, firstTimeDefaultOnly) =>
      set((state) => {
        if (state.cameraFirstResolvedPresetByCamera[cameraId]) {
          return {};
        }
        const selectedPreset = (state.presetsByCamera[cameraId] || []).find(
          (preset) => preset.localId === presetLocalId
        );
        const shouldHideDefault =
          firstTimeDefaultOnly && selectedPreset?.source === "custom";
        return {
          cameraFirstResolvedPresetByCamera: {
            ...state.cameraFirstResolvedPresetByCamera,
            [cameraId]: presetLocalId,
          },
          hideDefaultPresetForCamera: shouldHideDefault
            ? {
                ...state.hideDefaultPresetForCamera,
                [cameraId]: true,
              }
            : state.hideDefaultPresetForCamera,
        };
      }),
    getCurrentSchedule: () => {
      const { schedules, activeScheduleIndex } = get();
      return schedules[activeScheduleIndex];
    },
    getSelectedCamerasForSchedule: (scheduleId) =>
      get().selectedCameraIdsBySchedule[scheduleId] || [],
    getPresetsForCamera: (cameraId) => get().presetsByCamera[cameraId] || [],
    getSelectedPresetForScheduleCamera: (scheduleId, cameraId) => {
      const selectedPresetLocalId =
        get().selectedPresetByScheduleCamera[scheduleCameraKey(scheduleId, cameraId)];
      if (!selectedPresetLocalId) {
        return undefined;
      }
      return get()
        .getPresetsForCamera(cameraId)
        .find((preset) => preset.localId === selectedPresetLocalId);
    },
    submitAll: async () => {
      const {
        siteId,
        submitLoading,
        setEnabledForSite,
        schedules,
        selectedPresetByScheduleCamera,
        presetsByCamera,
      } = get();
      if (!siteId || submitLoading) {
        return false;
      }
      set({
        submitLoading: true,
        submitError: null,
      });
      const AxiosUI = createApi("");
      try {
        const siteStore = useSiteAboutStore.getState();
        let metricRows: MetricSimplifiedRow[] =
          siteStore.siteMetricsSimplifiedCustomerId === siteId
            ? siteStore.siteMetricsSimplified
            : [];
        if (metricRows.length === 0) {
          const metricSimplifiedResponse = await AxiosUI.get("metric/simplified/", {
            params: { customer: siteId },
          });
          if (Array.isArray(metricSimplifiedResponse.data?.results)) {
            metricRows = metricSimplifiedResponse.data.results;
          } else if (Array.isArray(metricSimplifiedResponse.data)) {
            metricRows = metricSimplifiedResponse.data;
          } else {
            metricRows = [];
          }
          siteStore.setSiteMetricsSimplifiedData(metricRows, siteId);
        }

        const scheduleOrder = schedules.map((schedule) => schedule.id);
        const cameraOrder = get().cameras.map((camera) => camera.id);

        const presetDedupeKey = (cameraId: number, presetLocalId: string) =>
          `${cameraId}:${presetLocalId}`;

        type SubmitCell = {
          scheduleId: number;
          cameraId: number;
          presetLocalId: string;
          presetName: string;
          zones: IgnoreZone[];
        };

        const submitCells: SubmitCell[] = [];

        cameraOrder.forEach((cameraId) => {
          scheduleOrder.forEach((scheduleId) => {
            const selectedPresetLocalId =
              selectedPresetByScheduleCamera[scheduleCameraKey(scheduleId, cameraId)];
            if (!selectedPresetLocalId) {
              throw new Error(
                `Missing preset selection for camera ${cameraId} on schedule ${scheduleId}.`
              );
            }
            if (selectedPresetLocalId === FLEX_IZ_DEFAULT_CAMERA_CONFIG_OPTION) {
              // Default: no preset to create; the assignment row is written
              // with `preset: null` in phase 3.
              submitCells.push({
                scheduleId,
                cameraId,
                presetLocalId: selectedPresetLocalId,
                presetName: "Default",
                zones: [],
              });
              return;
            }
            const selectedPreset = (presetsByCamera[cameraId] || []).find(
              (preset) => preset.localId === selectedPresetLocalId
            );
            if (!selectedPreset) {
              throw new Error(
                `Preset "${selectedPresetLocalId}" was not found for camera ${cameraId}.`
              );
            }
            submitCells.push({
              scheduleId,
              cameraId,
              presetLocalId: selectedPresetLocalId,
              presetName: selectedPreset.name?.trim() || "Flex Ignore Zones Preset",
              zones: selectedPreset.zones || [],
            });
          });
        });

        const uniquePresetKeys: string[] = [];
        const uniquePresetByKey = new Map<
          string,
          { cameraId: number; presetLocalId: string; presetName: string; zones: IgnoreZone[] }
        >();

        submitCells.forEach((cell) => {
          if (cell.presetLocalId === FLEX_IZ_DEFAULT_CAMERA_CONFIG_OPTION) {
            return;
          }
          const dk = presetDedupeKey(cell.cameraId, cell.presetLocalId);
          if (!uniquePresetByKey.has(dk)) {
            uniquePresetByKey.set(dk, {
              cameraId: cell.cameraId,
              presetLocalId: cell.presetLocalId,
              presetName: cell.presetName,
              zones: cell.zones,
            });
            uniquePresetKeys.push(dk);
          }
        });

        const createdPresetIdByDedupeKey = new Map<string, number>();

        // Phase 1: create each distinct (camera, preset) once.
        for (const dk of uniquePresetKeys) {
          const meta = uniquePresetByKey.get(dk);
          if (!meta) {
            continue;
          }
          const presetBody = { camera: meta.cameraId, name: meta.presetName };
          const createdPresetResponse = await AxiosUI.post("preset/", presetBody);
          const createdPresetId = Number(createdPresetResponse.data?.id);
          if (!Number.isFinite(createdPresetId)) {
            throw new Error(
              `Failed to create preset for camera ${meta.cameraId}; missing preset id in response.`
            );
          }
          createdPresetIdByDedupeKey.set(dk, createdPresetId);
        }

        // Phase 2: assign zones once per distinct (camera, preset).
        for (const dk of uniquePresetKeys) {
          const meta = uniquePresetByKey.get(dk);
          const createdPresetId = createdPresetIdByDedupeKey.get(dk);
          if (!meta || !createdPresetId) {
            continue;
          }
          if (
            meta.presetLocalId === FLEX_IZ_NO_IGNORE_ZONES_OPTION ||
            (meta.zones || []).length === 0
          ) {
            continue;
          }
          const presetZoneApiName = `${meta.presetName.trim()} #${createdPresetId}`;
          const presetZonePayload = buildPresetZonePayload({
            cameraId: meta.cameraId,
            presetId: createdPresetId,
            presetName: presetZoneApiName,
            zones: meta.zones,
            metricRows,
          });
          await AxiosUI.post("preset-zone/", presetZonePayload);
        }

        // Phase 3: map each schedule-camera selection to the shared preset id (skip no-ignore only).
        for (const cell of submitCells) {
          if (cell.presetLocalId === FLEX_IZ_DEFAULT_CAMERA_CONFIG_OPTION) {
            await AxiosUI.post("camera-schedule-preset/", {
              camera: cell.cameraId,
              preset: null,
              flex_schedule: cell.scheduleId,
            });
            continue;
          }
          const dk = presetDedupeKey(cell.cameraId, cell.presetLocalId);
          const createdPresetId = createdPresetIdByDedupeKey.get(dk);
          if (!createdPresetId) {
            continue;
          }
          const assignBody = {
            camera: cell.cameraId,
            preset: createdPresetId,
            flex_schedule: cell.scheduleId,
          };
          await AxiosUI.post("camera-schedule-preset/", assignBody);
        }

        // Presets and assignments are saved, but the connector only reads them
        // on the next settings deploy — surface the Sync control.
        markSiteUnsynced(String(siteId));

        set((state) => {
          const nextPresetsByCamera: Record<number, FlexPreset[]> = {
            ...state.presetsByCamera,
          };
          createdPresetIdByDedupeKey.forEach((createdPresetId, dedupeKey) => {
            const meta = uniquePresetByKey.get(dedupeKey);
            if (!meta) {
              return;
            }
            const cameraPresets = nextPresetsByCamera[meta.cameraId] || [];
            nextPresetsByCamera[meta.cameraId] = cameraPresets.map((preset) =>
              preset.localId === meta.presetLocalId
                ? {
                    ...preset,
                    persistedId: createdPresetId,
                  }
                : preset
            );
          });
          return {
            presetsByCamera: nextPresetsByCamera,
          };
        });

        const refreshResults = await Promise.allSettled(
          cameraOrder.map((cameraId) => get().loadCameraContext(cameraId, true))
        );
        refreshResults.forEach((result, index) => {
          if (result.status === "rejected") {
            const failedCameraId = cameraOrder[index];
            console.error(
              `Flex Ignore Zones onboarding: failed to refresh presets for camera ${failedCameraId} after submit.`,
              result.reason
            );
          }
        });

        setEnabledForSite(siteId, true);
        set({
          submitLoading: false,
          submitError: null,
          isOpen: false,
          step: "scheduleSelection",
          pendingEditorContext: null,
        });
        return true;
      } catch (error: any) {
        const conflictMessages = presetConflictMessagesFromError(error);
        set({
          submitLoading: false,
          submitError: conflictMessages
            ? conflictMessages.join("\n")
            : error?.message ||
              "Unable to save Flex Ignore Zones presets and schedule assignments.",
        });
        return false;
      }
    },
    persistFlexSchedulePresetAssignments: async (siteId, flexSchedules) => {
      const enabledForSite = get().enabledBySite[siteId] === true;
      if (!enabledForSite) {
        return { ok: true };
      }
      const camerasFromStore = get().cameras;
      const cameras: FlexCameraSummary[] =
        camerasFromStore.length > 0
          ? camerasFromStore
          : (() => {
              try {
                const raw = JSON.parse(
                  localStorage.getItem("siteCameras") || '{"results":[]}'
                );
                const results = Array.isArray(raw.results) ? raw.results : [];
                return results.map((camera: any) => ({
                  id: Number(camera.id),
                  camera_name: camera.camera_name,
                  preview_url: camera.preview_url,
                  ignore_zones: camera.ignore_zones,
                }));
              } catch {
                return [];
              }
            })();
      if (cameras.length === 0) {
        return { ok: true };
      }
      const { selectedPresetByScheduleCamera, presetsByCamera } = get();
      const AxiosUI = createApi("");
      let wroteAssignment = false;
      for (const schedule of flexSchedules) {
        const scheduleId = Number(schedule?.id);
        if (!Number.isFinite(scheduleId)) {
          continue;
        }
        for (const camera of cameras) {
          const key = scheduleCameraKey(scheduleId, camera.id);
          const presetLocalId = selectedPresetByScheduleCamera[key];
          if (!presetLocalId) {
            continue;
          }
          if (presetLocalId === FLEX_IZ_DEFAULT_CAMERA_CONFIG_OPTION) {
            // "Default": the row records that no preset applies, so the settings
            // generator falls back to the camera's own zones and default lines.
            try {
              await AxiosUI.post("camera-schedule-preset/", {
                camera: camera.id,
                preset: null,
                flex_schedule: scheduleId,
              });
              wroteAssignment = true;
            } catch (error: any) {
              if (wroteAssignment) {
                markSiteUnsynced(String(siteId));
              }
              return {
                ok: false,
                error:
                  presetConflictMessagesFromError(error)?.join("\n") ||
                  error?.message ||
                  "Unable to update flex ignore zone preset assignments for this site.",
              };
            }
            continue;
          }
          const preset = (presetsByCamera[camera.id] || []).find(
            (p) => p.localId === presetLocalId
          );
          if (!preset) {
            return {
              ok: false,
              error: `Could not find flex ignore zone preset for camera ${camera.camera_name || camera.id}. Refresh the page and try again.`,
            };
          }
          if (preset.persistedId == null) {
            return {
              ok: false,
              error: `Save preset "${preset.name}" for camera ${camera.camera_name || camera.id} before saving schedule changes.`,
            };
          }
          try {
            await AxiosUI.post("camera-schedule-preset/", {
              camera: camera.id,
              preset: preset.persistedId,
              flex_schedule: scheduleId,
            });
            wroteAssignment = true;
          } catch (error: any) {
            if (wroteAssignment) {
              markSiteUnsynced(String(siteId));
            }
            return {
              ok: false,
              error:
                presetConflictMessagesFromError(error)?.join("\n") ||
                error?.message ||
                "Unable to update flex ignore zone preset assignments for this site.",
            };
          }
        }
      }
      if (wroteAssignment) {
        // Assignments (Default included) only reach the connector on the next
        // settings deploy — turn the Sync control red.
        markSiteUnsynced(String(siteId));
      }
      return { ok: true };
    },
    getPresetConflictsForSite: (siteId, flexSchedules) => {
      const state = get();
      if (state.enabledBySite[siteId] !== true || state.siteId !== siteId) {
        return [];
      }
      return findPresetConflicts({
        schedules: flexSchedules,
        cameras: state.cameras,
        selectedPresetByScheduleCamera: state.selectedPresetByScheduleCamera,
        presetsByCamera: state.presetsByCamera,
        presetNamesByCamera: state.presetNamesByCamera,
        identity: "persisted",
      });
    },
    resetAll: () =>
      set((state) => ({
        ...initialState,
        enabledBySite: state.enabledBySite,
        flexIgnoreZonesEnabledSyncLoadingBySite: state.flexIgnoreZonesEnabledSyncLoadingBySite,
        flexIgnoreZonesEnabledSyncErrorBySite: state.flexIgnoreZonesEnabledSyncErrorBySite,
      })),
  }));

